import React, { useState, useEffect } from 'react';
import accountsService from '../../services/accounts';
import {
    PagingState,
    CustomPaging,
    SortingState,
    Sorting,
} from '@devexpress/dx-react-grid';
import {
    Grid,
    Table,
    TableHeaderRow,
    TableColumnResizing,
    PagingPanel,
} from '@devexpress/dx-react-grid-material-ui';
import CardHeader from '../Util/CardHeader';
import { useSnackbar } from 'notistack';
import styled from 'styled-components';
import AuditFilter from './AuditFilter';
import { Tooltip } from '@material-ui/core';
import SearchOutlined from '@material-ui/icons/SearchOutlined';
import moment, { Moment } from 'moment';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import useLocalStorage from '../Util/useLocalStorage';
import Date from '../Util/Date';
import { sortval, TSession } from '../Sessions/Sessions';

interface Props extends RouteComponentProps {}

export enum State {
    Upload,
    Prepare,
    Run,
    Label,
}
export type TAuditLog = {
    id: number;
    type?: string | null;
    timestamp: string;
    sessionId?: number | null;
    parameters?: { [key: string]: string } | null;
    user?: { id: number; name?: string | null } | null;
};

const userVal = (row: TAuditLog) => {
    return row.user ? row.user.name || String(row.user.id) : '';
};

const timestamp = (row: TAuditLog) => {
    let milliseconds;
    if (row.timestamp) {
        milliseconds = moment.utc(row.timestamp).unix() * 1000;
    }
    return <Date value={milliseconds} format="DD-MM-YYYY HH:mm:ss" />;
};

const sessionIdVal = (row: TAuditLog) => row.sessionId ?? '';

const paramsVal = (row: TAuditLog) =>
    Object.entries(row.parameters || {})
        .map(([key, value]) => key + '=' + value)
        .join(', ');

const sortingStateColumnExtensions = [
    { columnName: 'params', sortingEnabled: false },
];

const TitleWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
`;

const Body = styled.div`
    display: flex;
    flex-direction: row;
`;

const FilterContainer = styled.div`
    min-width: 300px;
    flex-grow: 1;
`;

const TableContainer = styled.div`
    flex-grow: 10;
    margin-left: 32px;
    width: calc(100vw - 550px);
`;

const Sessions: React.FunctionComponent<Props> = () => {
    const [sorting, setSorting] = useLocalStorage<Sorting[]>('audit-sort', [
        { columnName: 'timestamp', direction: 'desc' as 'desc' },
    ]);
    const [page, setPage] = useLocalStorage<number>('audit-page', 0);
    const [pageSize, setPageSize] = useLocalStorage<number>(
        'audit-pagesize',
        10
    );
    const [columnWidths, setColumnWidths] = useState<
        Array<{ columnName: string; width: number | string }>
    >([
        { columnName: 'timestamp', width: 200 },
        { columnName: 'type', width: 250 },
        { columnName: 'sessionid', width: 150 },
        { columnName: 'user', width: 150 },
        { columnName: 'params', width: 350 },
    ]);
    const [search, setSearch] = useState<string | undefined>();
    const [showFilter, setShowFilter] = useState(false);
    const [user, setUser] = useState<string | undefined>();
    const [session, setSession] = useState<TSession | undefined>();
    const [timestampRange, setTimestampRange] = useState<{
        startDate: Moment | null;
        endDate: Moment | null;
    }>({
        startDate: null,
        endDate: null,
    });
    let columns: any = [
        {
            name: 'timestamp',
            title: 'Timestamp',
            getCellValue: timestamp,
            prio: 1,
        },
        { name: 'type', title: 'Type', prio: 3 },
        { name: 'sessionid', title: 'Session ID', getCellValue: sessionIdVal, sort: 'sessionId', prio: 5 },
        { name: 'user', title: 'User', getCellValue: userVal, prio: 1 },
        { name: 'params', title: 'Parameters', getCellValue: paramsVal, prio: 1 },
    ];

    const [data, setData] = useState<{ total: number; items: TAuditLog[] } | null>(null);
    const [error, setError] = useState<unknown>(null);
    const sort = sorting[0];
    const orderBy = sort ? sortval(sort.columnName, columns) : 'timestamp';
    const sortOrder = sort?.direction === 'asc' ? 'Ascending' : 'Descending';
    const from = timestampRange.startDate?.toISOString();
    const to = timestampRange.endDate?.toISOString();
    const sessionId = session?.id;

    useEffect(() => {
        let active = true;
        const load = () =>
            accountsService
                .getAuditLogs({
                    offset: page * pageSize,
                    limit: pageSize,
                    orderBy,
                    sortOrder,
                    searchText: search || undefined,
                    from,
                    to,
                    userIds: user ? [user] : undefined,
                    sessionIds: sessionId ? [sessionId] : undefined,
                })
                .then(
                    (result: { total: number; items: TAuditLog[] }) => active && setData(result),
                    (err: unknown) => active && setError(err)
                );
        load();
        const interval = window.setInterval(load, 10000);
        return () => {
            active = false;
            window.clearInterval(interval);
        };
    }, [page, pageSize, orderBy, sortOrder, search, from, to, user, sessionId]);

    useEffect(() => {
        if (data && Math.floor(data.total / pageSize) < page) {
            setPage(0);
        }
    }, [data, page, pageSize, setPage]);

    const { enqueueSnackbar } = useSnackbar();

    if (error) {
        enqueueSnackbar('Failed to load sessions', {
            variant: 'error',
            autoHideDuration: 3000,
        });
    }

    const griddata = data?.items || [];
    const count = data?.total || 0;
    return (
        <div
            className="sciui-card sciui-m-l sciui-p-l sciui-bg-p-5"
            style={{ overflow: 'visible' }}
        >
            <TitleWrapper>
                <div style={{ display: 'flex' }}>
                    <CardHeader>Auditlog</CardHeader>
                    <div style={{ marginTop: '30px' }}>
                        <Tooltip title="Show filter">
                            <SearchOutlined
                                style={{ fontSize: 30, marginLeft: '16px', marginTop: '10px', cursor: 'pointer' }}
                                onClick={() => setShowFilter(!showFilter)}
                            />
                        </Tooltip>
                    </div>
                </div>
            </TitleWrapper>
            <Body>
                {showFilter && (
                <FilterContainer>
                    <AuditFilter
                        key="filter"
                        timestampRange={timestampRange}
                        onTimestampRangeChange={setTimestampRange}
                        search={search}
                        onSearchChange={setSearch}
                        user={user}
                        onChangeUser={setUser}
                        session={session}
                        onChangeSession={setSession}
                    />
                </FilterContainer>
                )}
                <TableContainer>
                    <Grid rows={griddata} columns={columns}>
                        <PagingState
                            currentPage={page}
                            onCurrentPageChange={setPage}
                            pageSize={pageSize}
                            onPageSizeChange={setPageSize}
                        />
                        <SortingState
                            sorting={sorting}
                            onSortingChange={setSorting}
                            columnExtensions={sortingStateColumnExtensions}
                        />
                        <CustomPaging totalCount={count} />
                        <Table
                            rowComponent={(row) => (
                                <Table.Row
                                    {...row}
                                    hover
                                    // eslint-disable-next-line no-alert
                                />
                            )}
                        />
                        <TableColumnResizing
                            columnWidths={columnWidths}
                            onColumnWidthsChange={setColumnWidths}
                        />
                        <TableHeaderRow showSortingControls />
                        <PagingPanel pageSizes={[10, 25, 50]} />
                    </Grid>
                </TableContainer>
            </Body>
        </div>
    );
};

export default withRouter(Sessions);
